import React, { useState, useRef, useEffect } from "react";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
    errorMessage,
    formatAmount,
    formatDate,
    formatStatus,
    formatUsername,
    permissionCssClass,
    ucFirst
} from "../../../util/Helpers";
import cogoToast from "cogo-toast";
import { Permissions, PermissionType } from "../../../util/Permissions";
import { useSelector } from "react-redux";
import DynamicSimpleTable from "../../../util/DynamicSimpleReactTable";
import { SubHeader } from "../../Common/layout/SubHeader";
import { Redirect, Switch } from "react-router-dom";
import { ContentRoute } from "../../../../_metronic/layout";
import { FilterGroup } from "../../Common/components/FilterGroup";
import ViewCardModal from "./modals/ViewCardModal";
import FreezeCardModal from "./modals/FreezeCardModal";
import UnfreezeCardModal from "./modals/UnfreezeCardModal";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { Button } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { Permission } from "../../Common/layout/Permission";
import { useDownloadCardsReportMutation, useDownloadCardTransactionsReportMutation } from "../../../../redux/apiSlice";
import CreationModal from "./modals/CreationModal";

export const CardManagement = (
    {
        match: {
            params: { userGroup },
        },
    }
) => {

    const adminUser = useSelector(state => state.auth.user);

    const tableRef = useRef();

    const [
        downloadCardsReport,
        {
            isLoading: isLoadingCardsReport,
            isSuccess: isCardReportGenerating,
        }
    ] = useDownloadCardsReportMutation()

    useEffect(() => {
        if (isCardReportGenerating) {
            cogoToast.success(`Cards export in progress. The resulting file will be sent to your mail.`);
        }
    }, [isCardReportGenerating])

    const [
        downloadCardTransactionsReport,
        {
            isLoading: isLoadingCardTransactionsReport,
            isSuccess: isCardTransactionsReportGenerating,
        }
    ] = useDownloadCardTransactionsReportMutation()

    useEffect(() => {
        if (isCardTransactionsReportGenerating) {
            cogoToast.success(`Card transactions export in progress. The resulting file will be sent to your mail.`);
        }
    }, [isCardTransactionsReportGenerating])

    const cardStatuses = ["verified", "pending", "processing", "active", "failed", "inactive", "completed", "cancelled", "executed", "complete"]
    const cardUserStatuses = ["pending", "processing", "active", "inactive"]

    const [cardModal, setCardModal] = useState({
        showViewCardModal: false,
        showFreezeCardModal: false,
        showUnfreezeCardModal: false,
        details: null
    });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setCardModal({ ...cardModal, [modal]: !cardModal[modal] })
    }

    const viewCard = details => {
        setCardModal({ ...cardModal, showViewCardModal: true, details });
    }
    const viewCreation = details => {
        setCardModal({ ...cardModal, showViewCreationModal: true, details });
    }

    /* Freeze Card Modal*/
    const freezeCard = details => {
        setCardModal({
            ...cardModal,
            showFreezeCardModal: true,
            details
        })
    }

    /* Unfreeze Card Modal*/
    const unfreezeCard = details => {
        setCardModal({
            ...cardModal,
            showUnfreezeCardModal: true,
            details
        })
    }
    /* Action Buttons */
    const cardCreation = (row) => {
        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <Permission permission={Permissions.CARDS} permissionType={PermissionType.READ}>
                    <Button
                        title="View Creation"
                        className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                        onClick={_ => viewCreation(row)}
                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <SVG
                                title="View Creation"
                                src={toAbsoluteUrl(
                                    "/media/svg/icons/General/Eye-2.svg"
                                )}
                            />
                        </span>
                    </Button>
                </Permission>


               
            </span>
        );
    };

    /* Action Buttons */
    const cardAction = (row) => {
        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <Permission permission={Permissions.CARDS} permissionType={PermissionType.READ}>
                    <Button
                        title="View Card"
                        className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                        onClick={_ => viewCard(row)}
                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <SVG
                                title="View Card"
                                src={toAbsoluteUrl(
                                    "/media/svg/icons/General/Eye-2.svg"
                                )}
                            />
                        </span>
                    </Button>
                </Permission>
                {row?.status !== "inactive"
                    ? <Button
                        title="Freeze Card"
                        className={`btn btn-icon btn-light btn-hover-primary btn-sm mx-3 ${permissionCssClass(adminUser, Permissions.CARDS, PermissionType.UPDATE)}`}
                        onClick={_ => freezeCard(row)}

                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <SVG
                                title="Freeze Card"
                                src={toAbsoluteUrl("/media/svg/icons/Weather/Snow.svg")}
                            />
                        </span>
                    </Button>
                    : <Button
                        title="Unfreeze Card"
                        className={`btn btn-icon btn-light btn-hover-primary btn-sm mx-3 ${permissionCssClass(adminUser, Permissions.CARDS, PermissionType.UPDATE)}`}
                        onClick={_ => unfreezeCard(row)}

                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <SVG
                                title="Unfreeze Card"
                                src={toAbsoluteUrl("/media/svg/icons/General/Fire.svg")}
                            />
                        </span>
                    </Button>
                }
            </span>
        );
    };

    return (
        <>
            <SubHeader>
                <SubHeader.Nav>
                    <SubHeader.NavItem label={"Cards"} to={`/cards/${userGroup}/all`} />
                    <SubHeader.NavItem label={"Card Users"} to={`/cards/${userGroup}/card-users`} />
                    <SubHeader.NavItem label={"Card Creations"} to={`/cards/${userGroup}/card-creations`} />
                </SubHeader.Nav>
            </SubHeader>
            <div className="py-4 px-6">
                <Switch>
                    <Redirect exact={true} from="/cards/:userGroup" to="/cards/:userGroup/all" />

                    <ContentRoute path="/cards/:userGroup/all" permission={Permissions.CARDS}>
                        <FilterGroup key={"cards"}>
                            <div className="d-flex">
                                <FilterGroup.Filter name={"type"} label={"card type"}>
                                    <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                    <FilterGroup.Option value={"VIRTUAL"}>virtual</FilterGroup.Option>
                                    <FilterGroup.Option
                                        value={"PHYSICAL"}>physical</FilterGroup.Option>
                                </FilterGroup.Filter>
                                <div className={"ml-2"}>
                                    <FilterGroup.Filter name={"status"} label={"card status"}>
                                        <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                        {cardStatuses.map((status) => {
                                            return <FilterGroup.Option key={status} value={status}>
                                                {status}</FilterGroup.Option>
                                        })}
                                    </FilterGroup.Filter>
                                </div>
                            </div>
                            <FilterGroup.Content className="mt-5">
                                {
                                    ({ filters }) => {
                                        return <Card>
                                            <CardHeader title={`Cards`}>
                                                <CardHeaderToolbar>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary mr-4"
                                                        disabled={isLoadingCardsReport}
                                                        onClick={() => downloadCardsReport({
                                                            userGroup,
                                                            type: filters?.type || "",
                                                            status: filters?.status || "",
                                                            format: "xlsx",
                                                        })}
                                                    >
                                                        Export XLSX
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        disabled={isLoadingCardsReport}
                                                        onClick={() => downloadCardsReport({
                                                            userGroup,
                                                            type: filters?.type || "",
                                                            status: filters?.status || "",
                                                            format: "csv",
                                                        })}
                                                    >
                                                        Export CSV
                                                    </button>
                                                </CardHeaderToolbar>
                                            </CardHeader>
                                            <CardBody>
                                                <DynamicSimpleTable
                                                    key={`${userGroup}-cards`}
                                                    queryParams={
                                                        `userGroup=${userGroup}`
                                                        + `&type=${filters?.type || ""}`
                                                        + `&status=${filters?.status || ""}`
                                                    }
                                                    searchBox={true}
                                                    source={{ url: `/cards` }}
                                                    columns={[
                                                        { title: "CARD NAME", key: "cardName" },
                                                        { title: "USERID", key: "userId" },
                                                        { title: "LAST 4", key: "last4" },
                                                        { title: "CURRENCY", key: "currency" },
                                                        { title: "TYPE", key: "cardType" },
                                                        { title: "BALANCE", format: row => formatAmount(row.currency, row.balance) },
                                                        { title: "EXPIRES ON", format: (row) => formatDate(row.expiry) },
                                                        { title: "STATUS", format: row => formatStatus(row.status) },
                                                        { title: "", format: cardAction }
                                                    ]}
                                                    ref={tableRef}
                                                    onError={(err) =>
                                                        cogoToast.error(errorMessage(err))
                                                    }
                                                    refreshIcon={true}
                                                />
                                            </CardBody>
                                        </Card>
                                    }
                                }
                            </FilterGroup.Content>
                        </FilterGroup>
                    </ContentRoute>
                    <ContentRoute path="/cards/:userGroup/card-users" permission={Permissions.CARDS}>
                        <FilterGroup key={"card-users"}>
                            <div className="d-flex">
                                <FilterGroup.Filter name={"status"} label={"status"}>
                                    <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                    {cardUserStatuses.map((status) => {
                                        return <FilterGroup.Option key={status} value={status}>
                                            {status}</FilterGroup.Option>
                                    })}
                                </FilterGroup.Filter>
                            </div>
                            <FilterGroup.Content className="mt-5">
                                {
                                    ({ filters }) => {
                                        return <Card>
                                            <CardHeader title={`Card Users`}/>
                                            <CardBody>
                                                <DynamicSimpleTable
                                                    key={`${userGroup}-card-users`}
                                                    queryParams={
                                                        `userGroup=${userGroup}`
                                                        + `&status=${filters?.status || ""}`
                                                    }
                                                    searchBox={true}
                                                    source={{ url: `/cards/users` }}
                                                    columns={[
                                                        { title: "ID", key: "id" },
                                                        { title: "USER", format: row => formatUsername(row.userId, row.username, null, null, "cards") },
                                                        { title: "PROVIDER", key: "provider" },
                                                        { title: "STATUS", format: row => formatStatus(row.status) },
                                                        { title: "CREATED AT", format: (row) => formatDate(row.created) },
                                                        { title: "UPDATED AT", format: (row) => formatDate(row.updated) },
                                                    ]}
                                                    ref={tableRef}
                                                    onError={(err) =>
                                                        cogoToast.error(errorMessage(err))
                                                    }
                                                    refreshIcon={true}
                                                />
                                            </CardBody>
                                        </Card>
                                    }
                                }
                            </FilterGroup.Content>
                        </FilterGroup>
                    </ContentRoute>
                    <ContentRoute path="/cards/:userGroup/card-creations" permission={Permissions.CARDS}>
                        <FilterGroup key={"card-users"}>
                            <div className="d-flex">
                                <FilterGroup.Filter name={"status"} label={"status"}>
                                    <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                    <FilterGroup.Option value={"pending"}>Pending</FilterGroup.Option>
                                    <FilterGroup.Option value={"completed"}>Completed</FilterGroup.Option>
                                    <FilterGroup.Option value={"failed"}>Failed</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            <FilterGroup.Content className="mt-5">
                                {
                                    ({ filters }) => {
                                        return <Card>
                                            <CardHeader title={`Card Creations`}/>
                                            <CardBody>
                                                <DynamicSimpleTable
                                                    key={`${userGroup}-card-creations`}
                                                    queryParams={
                                                        `userGroup=${userGroup}`
                                                        + `&status=${filters?.status || ""}`
                                                    }
                                                    searchBox={true}
                                                    source={{ url: `/cards/creations` }}
                                                    columns={[
                                                        { title: "ID", key: "id" },
                                                        {
                                                            title: "User",
                                                            format: (row) =>
                                                              formatUsername(
                                                                row.userId,
                                                                row.username,
                                                                null,
                                                                null,
                                                                "cards"
                                                              ),
                                                          },
                                                        { title: "PROVIDER", key: "provider" },
                                                        { title: "STATUS", format: row => formatStatus(row.status) },
                                                        { title: "CREATED AT", format: (row) => formatDate(row.created) },
                                                        { title: "UPDATED AT", format: (row) => formatDate(row.updated) },
                                                        { title: "", format: cardCreation }
                                                    ]}
                                                    ref={tableRef}
                                                    onError={(err) =>
                                                        cogoToast.error(errorMessage(err))
                                                    }
                                                    refreshIcon={true}
                                                />
                                            </CardBody>
                                        </Card>
                                    }
                                }
                            </FilterGroup.Content>
                        </FilterGroup>
                    </ContentRoute>
                </Switch>
            </div>

            <ViewCardModal
                {...cardModal}
                onHide={toggleModal("showViewCardModal")}
            />
            <CreationModal
                {...cardModal}
                onHide={toggleModal("showViewCreationModal")}
            />

            <FreezeCardModal
                {...cardModal}
                tableRef={tableRef}
                onHide={toggleModal("showFreezeCardModal")}
            />

            <UnfreezeCardModal
                {...cardModal}
                tableRef={tableRef}
                onHide={toggleModal("showUnfreezeCardModal")}
            />
        </>
    );
};
